import React, { createContext, useContext, useState, useEffect } from 'react'
import { authAPI, apiUtils } from '../services/api'
import { useLocalMode } from './LocalModeContext'
import tokenManager from '../services/tokenManager'
import googleDriveAPI from '../services/googleDriveAPI'

/**
 * Authentication Context for managing user state and auth operations.
 * 
 * Provides authentication state, login/logout functionality, and
 * user session management across the entire application.
 */

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [authToken, setAuthTokenState] = useState(null)
  const { isLocalMode, clearAllLocalData } = useLocalMode()

  // Initialize auth state from localStorage
  useEffect(() => {
    initializeAuth()
  }, [])

  // Listen for auth token expired events
  useEffect(() => {
    const handleAuthTokenExpired = (event) => {
      console.log('🔐 AuthContext: Auth token expired event received:', event.detail)
      const { reason, message, requiresReauth } = event.detail
      
      if (requiresReauth) {
        console.log('🔄 AuthContext: Triggering logout due to expired auth token')
        logout()
        
        // Show user-friendly message
        if (message) {
          // Use a simple alert for now - could be replaced with a toast
          alert(message)
        }
      }
    }

    window.addEventListener('authTokenExpired', handleAuthTokenExpired)
    
    return () => {
      window.removeEventListener('authTokenExpired', handleAuthTokenExpired)
    }
  }, [])

  /**
   * Initialize authentication state from localStorage
   */
  const initializeAuth = () => {
    try {
      const storedToken = localStorage.getItem('authToken')
      const storedUser = localStorage.getItem('user')
      const storedCloudAccessToken = localStorage.getItem('cloudAccessToken')
      const storedCloudRefreshToken = localStorage.getItem('cloudRefreshToken')

      if (storedToken && storedUser) {
        setAuthTokenState(storedToken)
        setUser(JSON.parse(storedUser))
        
        // Ensure token is also stored in localStorage for consistency
        localStorage.setItem('authToken', storedToken)
        
        // Log Google Drive token status
        if (storedCloudAccessToken) {
          console.log('✅ AuthContext: Found stored Google Drive access token')
          if (storedCloudRefreshToken) {
            console.log('✅ AuthContext: Found stored Google Drive refresh token')
          }
          
          // Force GoogleDriveAPI to check for stored tokens and initialize
          setTimeout(() => {
            googleDriveAPI.forceTokenCheck()
          }, 100) // Small delay to ensure component is fully mounted
        } else {
          console.log('ℹ️ AuthContext: No stored Google Drive tokens found')
        }
      }
    } catch (error) {
      console.error('Error initializing auth:', error)
      // Clear corrupted data
      tokenManager.clearAllTokens()
    } finally {
      setLoading(false)
    }
  }

  /**
   * Login user with email and password
   */
  const login = async (email, password) => {
    try {
      setLoading(true)
      
      // Call login API
      const response = await authAPI.login({ email, password })
      
      // Store auth data using TokenManager
      const token = response.token
      const userData = {
        ...response.user,
        needsOnboarding: response.needsOnboarding,
        planType: response.planType
      }
      
      setAuthTokenState(token)
      setUser(userData)
      
      // Store user data in localStorage for persistence
      localStorage.setItem('user', JSON.stringify(userData))
      localStorage.setItem('authToken', token)
      
      // Use TokenManager to store tokens
      tokenManager.setJWTToken(token)
      if (response.refreshToken) {
        tokenManager.setJWTRefreshToken(response.refreshToken)
        console.log('✅ AuthContext: Stored refresh token for long-term sessions (180 days)')
      }
      
      // Store Google Drive tokens if available (for frontend sync)
      if (response.cloudAccessToken) {
        // Use TokenManager to store tokens so expiry is also set
        tokenManager.setGoogleDriveToken(response.cloudAccessToken)
        console.log('✅ AuthContext: Stored Google Drive access token for frontend sync')
        
        if (response.cloudRefreshToken) {
          tokenManager.setGoogleDriveRefreshToken(response.cloudRefreshToken)
          console.log('✅ AuthContext: Stored Google Drive refresh token for frontend sync')
        }
        
        // Force GoogleDriveAPI to check for new tokens and initialize
        setTimeout(() => {
          googleDriveAPI.forceTokenCheck()
        }, 100) // Small delay to ensure localStorage is updated
      } else {
        console.log('ℹ️ AuthContext: No Google Drive tokens available for frontend sync')
      }
      
      return { 
        success: true, 
        data: response,
        needsOnboarding: response.needsOnboarding 
      }
      
    } catch (error) {
      console.error('Login error:', error)
      return { 
        success: false, 
        error: error.message || 'Login failed' 
      }
    } finally {
      setLoading(false)
    }
  }

  /**
   * Register a new user
   */
  const registerUser = async (userData) => {
    try {
      setLoading(true)
      
      // Call register API
      const response = await authAPI.register(userData)
      
      // Store auth data using TokenManager
      const token = response.token
      const user = {
        ...response.user,
        needsOnboarding: response.needsOnboarding,
        planType: response.planType
      }
      
      setAuthTokenState(token)
      setUser(user)
      
      // Store user data in localStorage for persistence
      localStorage.setItem('user', JSON.stringify(user))
      localStorage.setItem('authToken', token)
      
      // Use TokenManager to store tokens
      tokenManager.setJWTToken(token)
      if (response.refreshToken) {
        tokenManager.setJWTRefreshToken(response.refreshToken)
        console.log('✅ AuthContext: Stored refresh token for new user (180 days)')
      }
      
      // Store Google Drive tokens if available (for frontend sync)
      if (response.cloudAccessToken) {
        // Use TokenManager to store tokens so expiry is also set
        tokenManager.setGoogleDriveToken(response.cloudAccessToken)
        console.log('✅ AuthContext: Stored Google Drive access token for frontend sync')
        
        if (response.cloudRefreshToken) {
          tokenManager.setGoogleDriveRefreshToken(response.cloudRefreshToken)
          console.log('✅ AuthContext: Stored Google Drive refresh token for frontend sync')
        }
        
        // Force GoogleDriveAPI to check for new tokens and initialize
        setTimeout(() => {
          googleDriveAPI.forceTokenCheck()
        }, 100) // Small delay to ensure localStorage is updated
      } else {
        console.log('ℹ️ AuthContext: No Google Drive tokens available for frontend sync')
      }
      
      return { 
        success: true, 
        data: response,
        needsOnboarding: response.needsOnboarding 
      }
      
    } catch (error) {
      console.error('Registration error:', error)
      return { 
        success: false, 
        error: error.message || 'Registration failed' 
      }
    } finally {
      setLoading(false)
    }
  }

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      setLoading(true)
      
      // Clear auth state
      setAuthTokenState(null)
      setUser(null)
      
      // Clear localStorage
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
      localStorage.removeItem('cloudAccessToken')
      localStorage.removeItem('cloudRefreshToken')
      
      // Use TokenManager to clear all tokens
      tokenManager.clearAllTokens()
      
      // Clear local mode data if applicable
      if (isLocalMode) {
        console.log('🔍 AuthContext: Local mode detected, clearing all local data...')
        try {
          await clearAllLocalData()
          console.log('✅ AuthContext: Local data cleared successfully')
        } catch (error) {
          console.error('❌ AuthContext: Failed to clear local data:', error)
        }
      }
      
      console.log('✅ AuthContext: Logout completed successfully')
      
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = () => {
    return !!authToken && !!user
  }

  /**
   * Get current user
   */
  const getCurrentUser = () => {
    return user
  }

  /**
   * Get current auth token
   */
  const getAuthToken = () => {
    return authToken
  }

  /**
   * Update user data
   */
  const updateUser = (userData) => {
    setUser(userData)
    localStorage.setItem('user', JSON.stringify(userData))
  }

  /**
   * Refresh user session
   */
  const refreshSession = async () => {
    try {
      const newToken = await tokenManager.refreshJWTToken()
      if (newToken) {
        setAuthTokenState(newToken)
        localStorage.setItem('authToken', newToken)
        return { success: true, token: newToken }
      }
      return { success: false, error: 'Failed to refresh session' }
    } catch (error) {
      console.error('Session refresh error:', error)
      return { success: false, error: error.message }
    }
  }

  // Listen for token refresh events from TokenManager
  useEffect(() => {
    const handleTokenRefreshed = (event) => {
      const { token, userInfo, cloudAccessToken, cloudRefreshToken } = event.detail
      if (token) {
        setAuthTokenState(token)
        localStorage.setItem('authToken', token)
        console.log('✅ AuthContext: Token refreshed automatically')
      }
      if (userInfo) {
        setUser(userInfo)
        localStorage.setItem('user', JSON.stringify(userInfo))
      }
      
      // Handle Google Drive tokens if provided
      if (cloudAccessToken) {
        // Use TokenManager to store tokens so expiry is also set
        tokenManager.setGoogleDriveToken(cloudAccessToken)
        console.log('✅ AuthContext: Google Drive access token refreshed automatically')
        
        if (cloudRefreshToken) {
          tokenManager.setGoogleDriveRefreshToken(cloudRefreshToken)
          console.log('✅ AuthContext: Google Drive refresh token refreshed automatically')
        }
        
        // Force GoogleDriveAPI to check for new tokens and initialize
        setTimeout(() => {
          googleDriveAPI.forceTokenCheck()
        }, 100) // Small delay to ensure localStorage is updated
      }
    }

    window.addEventListener('tokenRefreshed', handleTokenRefreshed)
    
    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefreshed)
    }
  }, [])

  const value = {
    user,
    loading,
    authToken,
    isAuthenticated: isAuthenticated(),
    login,
    registerUser,
    logout,
    getCurrentUser,
    getAuthToken,
    updateUser,
    refreshSession
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 